import {
  IonAlert,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
} from "@ionic/react";
import {
  arrowBackCircleOutline,
  closeCircleOutline,
  pauseOutline,
} from "ionicons/icons";
import React, { memo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { OrderStatus } from "./Constants";
import {
  MetricConstants,
  MetricTypeConstants,
} from "../../../components/Constants";
import config from "../../../components/helpers/socket/config";
import firebaseEventConfig from "../../../utils/firebaseAnalytics/firebaseEventConfig";
import { CMEDfirebaseLogEvent } from "../../../utils/firebaseAnalytics/firebaseUtils";
import { startMetric } from "../../../utils/metric-utils/metricUtils";
import { send } from "../../../utils/sockets/asyncSocket";
import { hapticsImpactMedium } from "../../../utils/core-utils/hapticUtils";

/**
 * @author
 * E45597 Aryamitra Chaudhuri
 */

type OneListItemProps = {
  side?: string;
  contract?: string;
  orderStatus?: number;
  orderId: any;
  price?: number;
  fills?: any;
  bid?: string;
  bidQty?: string;
  offer?: string;
  offerQty?: string;
  index: number;
  setOpenOrderViewModal: any;
  setOrderId: any;
  setOrderLoading: any;
  setMarketDataLoading: any;
  setFillsDataLoading: any;
};
const OneListItem: React.FC<OneListItemProps> = (props) => {
  const {
    side,
    contract,
    orderStatus,
    orderId,
    price,
    fills,
    bid,
    bidQty,
    offer,
    offerQty,
    index,
    setOpenOrderViewModal,
    setOrderId,
    setOrderLoading,
    setMarketDataLoading,
    setFillsDataLoading,
  } = props;

  const [isHoldOrderAlertOpen, setIsHoldOrderAlertOpen] =
    useState<boolean>(false);
  const [isKillOrderAlertOpen, setIsKillOrderAlertOpen] =
    useState<boolean>(false);
  const [isActivateOrderAlertOpen, setIsActivateOrderAlertOpen] =
    useState<boolean>(false);

  const statusGetter = (orderStatus: number | undefined) => {
    switch (orderStatus) {
      case 0:
      case 3:
      case 8:
        return OrderStatus.HELD;
      case 1:
      case 2:
        return OrderStatus.WORKING;
      case 7:
        return OrderStatus.PENDING;
      case 5:
        return OrderStatus.FILLED;
      case 4:
      case 6:
        return OrderStatus.CANCELLED;
      default:
        return OrderStatus.HELD;
    }
  };

  const statusColorGetter = (orderStatus: number | undefined) => {
    switch (orderStatus) {
      case 0:
      case 3:
      case 8:
        return "ob__status_medium";
      case 1:
      case 2:
        return "ob__status_warning";
      case 7:
        return "ob__status_dark";
      case 5:
        return "ob__status_success";
      case 4:
      case 6:
        return "ob__status_danger";
      default:
        return "ob__status_medium";
    }
  };

  const orderBookSlidingItem = document.querySelector("ion-item-sliding");

  const createOrderActionPayload = (action: any, id: any) => ({
    action: `cmed/selectedorder/${action}`,
    requestID: uuidv4(),
    type: `${action}SelectedOrderRequest`,
    payload: {
      orderId: id,
    },
  });

  const createOrderActionForActivateOrderPayload = (action: any, id: any) => ({
    action: `cmed/selectedorder/${action}`,
    requestID: uuidv4(),
    type: `${action}SelectedOrderRequest`,
    payload: {
      orderId: id,
      ignorePriceWarnings: "false",
    },
  });

  const killOrderHandler = () => {
    startMetric(MetricConstants.ORDER_KILL_LATENCY, MetricTypeConstants.TIMER);
    send(
      config.socket.channels.TRADING,
      createOrderActionPayload("cancel", orderId),
    );
    if (orderBookSlidingItem) {
      orderBookSlidingItem.close();
    }
    CMEDfirebaseLogEvent(
      firebaseEventConfig.CMEDComponentNames.CMED_ORDER_BOOK,
      firebaseEventConfig.eventNames.CMED_ORDER_BOOK.CMED_ORDER_BOOK_KILL_ORDER,
    );
  };
  const holdOrderHandler = () => {
    startMetric(MetricConstants.ORDER_HOLD_LATENCY, MetricTypeConstants.TIMER);
    send(
      config.socket.channels.TRADING,
      createOrderActionPayload("hold", orderId),
    );
    if (orderBookSlidingItem) {
      orderBookSlidingItem.close();
    }
    CMEDfirebaseLogEvent(
      firebaseEventConfig.CMEDComponentNames.CMED_ORDER_BOOK,
      firebaseEventConfig.eventNames.CMED_ORDER_BOOK.CMED_ORDER_BOOK_HOLD_ORDER,
    );
  };

  const activateOrderHandler = () => {
    startMetric(
      MetricConstants.ORDER_ACTIVATE_LATENCY,
      MetricTypeConstants.TIMER,
    );
    send(
      config.socket.channels.TRADING,
      createOrderActionForActivateOrderPayload("activate", orderId),
    );
    if (orderBookSlidingItem) {
      orderBookSlidingItem.close();
    }
    CMEDfirebaseLogEvent(
      firebaseEventConfig.CMEDComponentNames.CMED_ORDER_BOOK,
      firebaseEventConfig.eventNames.CMED_ORDER_BOOK
        .CMED_ORDER_BOOK_ACTIVE_ORDER,
    );
  };

  const handleClickSlidingId = () => {
    document.querySelector("ion-item-sliding")?.closeOpened();
  };

  const killOrder = () => {
    setIsKillOrderAlertOpen(true);
    handleClickSlidingId();
  };

  const holdOrder = () => {
    setIsHoldOrderAlertOpen(true);
    handleClickSlidingId();
  };

  const activateOrder = () => {
    setIsActivateOrderAlertOpen(true);
    handleClickSlidingId();
  };

  return (
    <>
      <IonItemSliding className="animate__animated animate__fadeIn">
        <IonItem
          button
          detail={false}
          className="ion-no-padding"
          lines="none"
          style={{
            "--inner-padding-end": 0,
            "--min-height": "70px",
            "--background": "#112B4A",
            marginBottom: "2px",
          }}
          onClick={() => {
            setOpenOrderViewModal(true);
            setOrderId(orderId);
            setOrderLoading(true);
            setMarketDataLoading(true);
            setFillsDataLoading(true);
            hapticsImpactMedium();
            CMEDfirebaseLogEvent(
              firebaseEventConfig.CMEDComponentNames.CMED_ORDER_BOOK,
              firebaseEventConfig.eventNames.CMED_ORDER_BOOK
                .CMED_ORDER_BOOK_VIEW_ORDER,
            );
          }}
        >
          <div className="custom_orderbook_item">
            <div className="ob__contract_side_status_section">
              <div className="ob__contract_sub-section">{contract}</div>
              <div className="ob__side_status_sub-section">
                <div
                  className={`ob__side_sub-section ${
                    side?.toUpperCase() === "BUY"
                      ? "ob__side_type_buy"
                      : "ob__side_type_sell"
                  }`}
                >
                  {side?.toUpperCase() === "BUY" ? "B" : "S"}
                </div>
                <div
                  className={`ob__status_sub-section ${statusColorGetter(
                    orderStatus,
                  )}`}
                >
                  {statusGetter(orderStatus)}
                </div>
              </div>
            </div>
            <div className="ob__price_fills_section">
              <div className="ob__price_sub-section">{price}</div>
              <div className="ob__fills_sub-section">{fills}</div>
            </div>
            <div className="ob__bid_bidqty_section">
              <div className="ob__bid_sub-section">{bid}</div>
              <div className="ob__bidqty_sub-section">
                {bidQty && `${bidQty}`}
              </div>
            </div>
            <div className="ob__offer_offerqty_section">
              <div className="ob__offer_sub-section">{offer}</div>
              <div className="ob__offerqty_sub-section">
                {offerQty && `${offerQty}`}
              </div>
            </div>
          </div>
        </IonItem>
        <IonItemOptions>
          {statusGetter(orderStatus) === "Held" ? (
            <IonItemOption color="success" onClick={() => activateOrder()}>
              <IonIcon slot="icon-only" icon={arrowBackCircleOutline}></IonIcon>
            </IonItemOption>
          ) : (
            <IonItemOption
              color="medium"
              onClick={() => holdOrder()}
              disabled={
                statusGetter(orderStatus) === "Filled" ||
                statusGetter(orderStatus) === "Cancelled"
              }
            >
              <IonIcon slot="icon-only" icon={pauseOutline}></IonIcon>
            </IonItemOption>
          )}
          <IonItemOption
            color="danger"
            onClick={() => killOrder()}
            disabled={
              statusGetter(orderStatus) === "Filled" ||
              statusGetter(orderStatus) === "Cancelled"
            }
          >
            <IonIcon slot="icon-only" icon={closeCircleOutline}></IonIcon>
          </IonItemOption>
        </IonItemOptions>
      </IonItemSliding>
      {/* hold order alert*/}
      <IonAlert
        className="one-alert"
        isOpen={isHoldOrderAlertOpen}
        header="Hold Order"
        message={`Are you sure you want to hold ${contract} ?`}
        onDidDismiss={() => setIsHoldOrderAlertOpen(false)}
        buttons={[
          {
            text: "No",
            cssClass: "alert-button-cancel",
          },
          {
            text: "Yes",
            cssClass: "alert-button-confirm",
            handler: () => {
              holdOrderHandler();
            },
          },
        ]}
      ></IonAlert>
      {/* kill order alert*/}
      <IonAlert
        className="one-alert"
        isOpen={isKillOrderAlertOpen}
        header="Kill Order"
        message={`Are you sure you want to kill ${contract} ?`}
        onDidDismiss={() => setIsKillOrderAlertOpen(false)}
        buttons={[
          {
            text: "No",
            cssClass: "alert-button-cancel",
          },
          {
            text: "Yes",
            cssClass: "alert-button-confirm",
            handler: () => {
              killOrderHandler();
              handleClickSlidingId();
            },
          },
        ]}
      ></IonAlert>
      {/* activate order alert*/}
      <IonAlert
        className="one-alert"
        isOpen={isActivateOrderAlertOpen}
        header="Activate Order"
        message={`Are you sure you want to activate ${contract} ?`}
        onDidDismiss={() => setIsActivateOrderAlertOpen(false)}
        buttons={[
          {
            text: "No",
            cssClass: "alert-button-cancel",
          },
          {
            text: "Yes",
            cssClass: "alert-button-confirm",
            handler: () => {
              activateOrderHandler();
            },
          },
        ]}
      ></IonAlert>
    </>
  );
};

export default memo(OneListItem);
